// src/pages/CartPage.jsx
import React, { useMemo, useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";
import useCart from "../hooks/useCart";

export default function CartPage() {
  const { items, removeItem, updateQty, clearCart } = useCart();

  const [customer, setCustomer] = useState({
    fullName: "",
    phone: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const totalAmount = useMemo(
    () =>
      items.reduce(
        (sum, it) => sum + (Number(it.price) || 0) * it.quantity,
        0
      ),
    [items]
  );

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
      }),
    []
  );

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const validateCustomer = () => {
    const newErrors = {};

    if (!customer.fullName.trim()) {
      newErrors.fullName = "Le nom complet est obligatoire.";
    }

    const phone = customer.phone.replace(/\s+/g, "");
    if (!phone) {
      newErrors.phone = "Le numéro de téléphone est obligatoire.";
    } else if (!/^\d{8,15}$/.test(phone)) {
      newErrors.phone =
        "Numéro de téléphone invalide (8 à 15 chiffres, sans lettres).";
    }

    if (customer.email.trim()) {
      const email = customer.email.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = "Adresse email invalide.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckout = async () => {
    if (!items.length) {
      alert("Votre panier est vide.");
      return;
    }

    if (!validateCustomer()) return;

    setLoading(true);
    try {
      const identifier = `ORD-${Date.now()}`;
      const totalItems = items.reduce((sum, it) => sum + it.quantity, 0);

      // 1️⃣ Enregistrer la commande en base
      const orderData = {
        identifier,
        amount: totalAmount,
        currency: "XOF",
        status: "pending",
        customer: {
          fullName: customer.fullName.trim(),
          phone: customer.phone.replace(/\s+/g, ""),
          email: customer.email.trim() || null,
        },
        items: items.map((it) => ({
          productId: it.id,
          name: it.name,
          slug: it.slug,
          price: Number(it.price) || 0,
          quantity: it.quantity,
          subtotal: (Number(it.price) || 0) * it.quantity,
          mainImage: it.mainImage || null,
        })),
        totalItems,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        paygate: {
          status: "pending",
        },
      };

      await addDoc(collection(db, "orders_prosoft"), orderData);

      // 2️⃣ Demander AU SERVEUR de préparer l'URL PayGate
      const payload = {
        amount: orderData.amount,
        identifier,
        description: `Commande ProSoft (${totalItems} article${
          totalItems > 1 ? "s" : ""
        })`,
        phone: customer.phone,
        successUrl: `${window.location.origin}/paygate-retour`,
        // network: "FLOOZ" ou "TMONEY" si tu veux forcer un réseau
      };

      const resp = await fetch("/api/paygate-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await resp.json();
      } catch (e) {
        console.error("Réponse non JSON de /api/paygate-session :", e);
      }

      if (!resp.ok || !data || !data.ok || !data.paygateUrl) {
        console.error("Réponse inattendue de /api/paygate-session :", {
          status: resp.status,
          data,
        });
        alert(
          "Impossible d'initialiser le paiement avec PayGate pour le moment. Merci de réessayer."
        );
        return;
      }

      // 🧪 Debug : on log l’URL PayGate
      console.log("🔗 URL PayGate générée :", data.paygateUrl);

      // 3️⃣ Redirection vers PayGate
      window.location.href = data.paygateUrl;
    } catch (error) {
      console.error(error);
      alert(
        "Impossible d'initialiser le paiement avec PayGate pour le moment. Merci de réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleQtyChange = (item, value) => {
    const qty = Number(value) || 1;
    if (qty <= 0) return;
    updateQty(item.id, qty);
  };

  if (!items.length) {
    return (
      <div className="cart-page py-4 bg-white">
        <div className="container">
          <h5 className="fw-bold mb-3 text-uppercase">Votre panier</h5>
          <p className="text-muted mb-3">Votre panier est vide.</p>
          <a href="/" className="btn btn-outline-dark btn-sm">
            Retour à la boutique
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page py-4 bg-white">
      <div className="container">
        <h5 className="fw-bold mb-3 text-uppercase">Votre panier</h5>

        <div className="row g-4">
          {/* Liste des articles */}
          <div className="col-lg-8">
            <div className="table-responsive small">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Prix</th>
                    <th style={{ width: 90 }}>Qté</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {it.mainImage && (
                            <img
                              src={it.mainImage}
                              alt={it.name}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                          )}
                          <div>
                            <div className="fw-semibold">{it.name}</div>
                            {it.brand && (
                              <div className="text-muted small">
                                {it.brand}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{fmt.format(it.price)} CFA</td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          className="form-control form-control-sm"
                          value={it.quantity}
                          onChange={(e) =>
                            handleQtyChange(it, e.target.value)
                          }
                        />
                      </td>
                      <td>
                        {fmt.format(
                          (Number(it.price) || 0) * it.quantity
                        )}{" "}
                        CFA
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeItem(it.id)}
                        >
                          Retirer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 mt-2 small"
              onClick={clearCart}
            >
              Vider le panier
            </button>
          </div>

          {/* Bloc infos client + résumé + bouton PayGate */}
          <div className="col-lg-4">
            <div className="card shadow-sm border-0">
              <div className="card-body small">
                <h6 className="fw-bold mb-3">Informations client</h6>

                <div className="mb-2">
                  <label className="form-label">Nom complet *</label>
                  <input
                    type="text"
                    name="fullName"
                    className="form-control form-control-sm"
                    value={customer.fullName}
                    onChange={handleCustomerChange}
                  />
                  {errors.fullName && (
                    <div className="text-danger small">{errors.fullName}</div>
                  )}
                </div>

                <div className="mb-2">
                  <label className="form-label">
                    Téléphone (Flooz / TMoney) *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-control form-control-sm"
                    value={customer.phone}
                    onChange={handleCustomerChange}
                    placeholder="Ex : 90909090"
                  />
                  {errors.phone && (
                    <div className="text-danger small">{errors.phone}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Email <span className="text-muted">(optionnel)</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="form-control form-control-sm"
                    value={customer.email}
                    onChange={handleCustomerChange}
                    placeholder="Ex : client@example.com"
                  />
                  {errors.email && (
                    <div className="text-danger small">{errors.email}</div>
                  )}
                </div>

                <hr />

                <h6 className="fw-bold mb-2">Récapitulatif</h6>
                <div className="d-flex justify-content-between mb-1">
                  <span>Sous-total</span>
                  <span>{fmt.format(totalAmount)} CFA</span>
                </div>
                <div className="d-flex justify-content-between mb-3 text-muted">
                  <span>Frais de livraison</span>
                  <span>À définir</span>
                </div>

                <div className="alert alert-light border small py-2">
                  Le paiement sera traité via{" "}
                  <strong>PayGate (Flooz / TMoney)</strong>.
                  <br />
                  Vous serez redirigé vers une page sécurisée pour valider le
                  paiement.
                </div>

                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={handleCheckout}
                  disabled={loading}
                >
                  {loading
                    ? "Redirection vers PayGate..."
                    : "Payer avec PayGate"}
                </button>

                <a
                  href="/"
                  className="btn btn-outline-secondary w-100 mt-2 small"
                >
                  Continuer vos achats
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
